import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Lead, LeadVisit } from "../../../models/Lead";
import { crm } from "./crmTheme";

export type CalendarVisit = LeadVisit & { lead: Lead };

type ViewMode = "month" | "week";

const DOW_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const HOUR_START = 6;
const HOUR_END = 23;
const HOUR_HEIGHT = 60; // px per hour
const TIME_GUTTER_W = "52px";
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

// ── Month chip ───────────────────────────────────────────────────────────────
const VisitChip = ({
  visit,
  onClick,
}: {
  visit: CalendarVisit;
  onClick: () => void;
}) => (
  <Box
    bg={crm.brandSoft}
    border="1px solid"
    borderColor={`${crm.brand}44`}
    borderLeft={`3px solid ${crm.brand}`}
    borderRadius="4px"
    px="4px"
    py="1px"
    fontSize="10px"
    cursor="pointer"
    _hover={{ bg: `${crm.brand}18` }}
    onClick={onClick}
    overflow="hidden"
    whiteSpace="nowrap"
    textOverflow="ellipsis"
    color={crm.brand}
  >
    {DateTime.fromISO(visit.scheduledDate).toFormat("HH:mm")} —{" "}
    {visit.lead.name ?? "Sem nome"}
  </Box>
);

// ── Week time-grid block ─────────────────────────────────────────────────────
const VisitBlock = ({
  visit,
  onClick,
}: {
  visit: CalendarVisit;
  onClick: () => void;
}) => {
  const dt = DateTime.fromISO(visit.scheduledDate);
  const hoursFromStart = dt.hour + dt.minute / 60 - HOUR_START;
  const top = hoursFromStart * HOUR_HEIGHT;

  return (
    <Tooltip
      label={`${dt.toFormat("HH:mm")} — ${visit.lead.name ?? "Sem nome"}${visit.responsibleName ? ` · ${visit.responsibleName}` : ""}`}
      placement="top"
      hasArrow
    >
      <Box
        position="absolute"
        top={`${top}px`}
        left="2px"
        right="2px"
        h="52px"
        bg={crm.brandSoft}
        border="1px solid"
        borderColor={`${crm.brand}44`}
        borderLeft={`3px solid ${crm.brand}`}
        borderRadius="4px"
        px="6px"
        py="4px"
        cursor="pointer"
        zIndex={0}
        _hover={{ bg: `${crm.brand}18`, zIndex: 1 }}
        onClick={onClick}
        overflow="hidden"
        lineHeight="1.3"
      >
        <Text fontWeight="600" fontSize="11px" noOfLines={1} color={crm.brand}>
          {dt.toFormat("HH:mm")} {visit.lead.name ?? "Sem nome"}
        </Text>
        {visit.responsibleName && (
          <Text fontSize="10px" color={crm.textMuted} noOfLines={1}>
            {visit.responsibleName}
          </Text>
        )}
      </Box>
    </Tooltip>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const VisitCalendar = ({
  visits,
  onVisitClick,
}: {
  visits: CalendarVisit[];
  onVisitClick: (v: CalendarVisit) => void;
}) => {
  const [anchorDate, setAnchorDate] = useState(DateTime.now().startOf("day"));
  const [viewMode, setViewMode] = useState<ViewMode>("month");

  const today = DateTime.now().toISODate();

  const visitsByDay = useMemo(() => {
    const map: Record<string, CalendarVisit[]> = {};
    for (const v of visits) {
      const key = DateTime.fromISO(v.scheduledDate).toISODate()!;
      if (!map[key]) map[key] = [];
      map[key].push(v);
    }
    return map;
  }, [visits]);

  // ── Month view data ──────────────────────────────────────────────────────
  const currentMonth = anchorDate.startOf("month");

  const weeks = useMemo(() => {
    const start = currentMonth;
    const daysInMonth = currentMonth.daysInMonth!;
    const offset = start.weekday % 7;
    const totalCells = Math.ceil((offset + daysInMonth) / 7) * 7;
    const cells = Array.from({ length: totalCells }, (_, i) => {
      const dayNum = i - offset + 1;
      return dayNum >= 1 && dayNum <= daysInMonth ? start.set({ day: dayNum }) : null;
    });
    const result: (DateTime | null)[][] = [];
    for (let i = 0; i < cells.length; i += 7) result.push(cells.slice(i, i + 7));
    return result;
  }, [currentMonth]);

  // ── Week view data ───────────────────────────────────────────────────────
  const weekStart = useMemo(
    () => anchorDate.minus({ days: anchorDate.weekday % 7 }),
    [anchorDate]
  );

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => weekStart.plus({ days: i })),
    [weekStart]
  );

  // ── Navigation ───────────────────────────────────────────────────────────
  const goBack = () =>
    setAnchorDate((d) =>
      viewMode === "month" ? d.minus({ months: 1 }) : d.minus({ weeks: 1 })
    );
  const goForward = () =>
    setAnchorDate((d) =>
      viewMode === "month" ? d.plus({ months: 1 }) : d.plus({ weeks: 1 })
    );
  const goToday = () => setAnchorDate(DateTime.now().startOf("day"));

  // ── Header label ─────────────────────────────────────────────────────────
  const headerLabel =
    viewMode === "month"
      ? currentMonth.setLocale("pt-BR").toFormat("MMMM yyyy")
      : (() => {
          const weekEnd = weekStart.plus({ days: 6 });
          const sameMonth = weekStart.month === weekEnd.month;
          const sameYear = weekStart.year === weekEnd.year;
          const startFmt = sameMonth
            ? weekStart.toFormat("d")
            : sameYear
            ? weekStart.setLocale("pt-BR").toFormat("d MMM")
            : weekStart.setLocale("pt-BR").toFormat("d MMM yyyy");
          return `${startFmt} — ${weekEnd.setLocale("pt-BR").toFormat("d MMM yyyy")}`;
        })();

  return (
    <Box overflowY="auto" maxH="calc(100vh - 260px)">
      {/* Controls */}
      <Flex align="center" justify="space-between" mb="16px" flexWrap="wrap" gap="8px">
        <Flex align="center" gap="8px">
          <IconButton
            aria-label="Período anterior"
            icon={<FaChevronLeft />}
            size="sm"
            variant="ghost"
            color={crm.textMuted}
            _hover={{ color: crm.text, bg: crm.subtle }}
            onClick={goBack}
          />
          <Text
            fontWeight="600"
            textTransform="capitalize"
            fontSize="md"
            minW="180px"
            textAlign="center"
            color={crm.text}
          >
            {headerLabel}
          </Text>
          <IconButton
            aria-label="Próximo período"
            icon={<FaChevronRight />}
            size="sm"
            variant="ghost"
            color={crm.textMuted}
            _hover={{ color: crm.text, bg: crm.subtle }}
            onClick={goForward}
          />
          <Button
            size="sm"
            variant="outline"
            borderColor={crm.border}
            color={crm.text}
            _hover={{ bg: crm.brandSoft, borderColor: crm.brand }}
            borderRadius="8px"
            onClick={goToday}
          >
            Hoje
          </Button>
        </Flex>

        {/* Segmented view toggle */}
        <Flex
          bg={crm.subtle}
          p="4px"
          borderRadius="10px"
          border={`1px solid ${crm.border}`}
          gap="2px"
        >
          {(["month", "week"] as ViewMode[]).map((mode) => (
            <Button
              key={mode}
              size="sm"
              borderRadius="8px"
              px="16px"
              py="6px"
              fontSize="sm"
              fontWeight={viewMode === mode ? "600" : "400"}
              color={viewMode === mode ? crm.brand : crm.textMuted}
              bg={viewMode === mode ? crm.surface : "transparent"}
              boxShadow={viewMode === mode ? crm.shadowSm : "none"}
              _hover={{ color: viewMode === mode ? crm.brand : crm.text }}
              transition="all .15s"
              onClick={() => setViewMode(mode)}
            >
              {mode === "month" ? "Mês" : "Semana"}
            </Button>
          ))}
        </Flex>
      </Flex>

      {/* ── Month view ── */}
      {viewMode === "month" && (
        <Box
          border={`1px solid ${crm.border}`}
          borderRadius="14px"
          overflow="hidden"
          boxShadow={crm.shadowSm}
        >
          <Flex>
            {DOW_LABELS.map((d) => (
              <Box
                key={d}
                flex={1}
                py="8px"
                textAlign="center"
                fontSize="xs"
                fontWeight="700"
                color={crm.textMuted}
                borderBottom={`1px solid ${crm.border}`}
                bg={crm.subtle}
                textTransform="uppercase"
                letterSpacing="0.04em"
              >
                {d}
              </Box>
            ))}
          </Flex>

          {weeks.map((week, wi) => (
            <Flex
              key={wi}
              borderBottom={wi < weeks.length - 1 ? `1px solid ${crm.border}` : undefined}
            >
              {week.map((day, di) => {
                const isoDate = day?.toISODate() ?? null;
                const dayVisits = isoDate ? (visitsByDay[isoDate] ?? []) : [];
                const isToday = isoDate === today;
                return (
                  <Box
                    key={di}
                    flex={1}
                    minH="80px"
                    p="4px"
                    borderLeft={di > 0 ? `1px solid ${crm.border}` : undefined}
                    bg={!day ? crm.subtle : undefined}
                  >
                    {day && (
                      <>
                        <Flex
                          w="24px"
                          h="24px"
                          align="center"
                          justify="center"
                          borderRadius="full"
                          bg={isToday ? crm.brand : undefined}
                          color={isToday ? "white" : crm.textMuted}
                          fontSize="xs"
                          fontWeight={isToday ? "700" : "400"}
                          mb="4px"
                        >
                          {day.day}
                        </Flex>
                        <Flex flexDir="column" gap="2px">
                          {dayVisits.map((v) => (
                            <VisitChip
                              key={v.id}
                              visit={v}
                              onClick={() => onVisitClick(v)}
                            />
                          ))}
                        </Flex>
                      </>
                    )}
                  </Box>
                );
              })}
            </Flex>
          ))}
        </Box>
      )}

      {/* ── Week view (Google-style time grid) ── */}
      {viewMode === "week" && (
        <Box
          border={`1px solid ${crm.border}`}
          borderRadius="14px"
          overflow="hidden"
          boxShadow={crm.shadowSm}
        >
          {/* Day header row */}
          <Flex
            borderBottom={`2px solid ${crm.border}`}
            bg={crm.surface}
          >
            {/* Gutter placeholder */}
            <Box
              w={TIME_GUTTER_W}
              flexShrink={0}
              borderRight={`1px solid ${crm.border}`}
              bg={crm.subtle}
            />

            {weekDays.map((day, di) => {
              const isoDate = day.toISODate()!;
              const isToday = isoDate === today;
              return (
                <Flex
                  key={di}
                  flex={1}
                  flexDir="column"
                  align="center"
                  py="8px"
                  gap="2px"
                  borderLeft={di > 0 ? `1px solid ${crm.border}` : undefined}
                  bg={isToday ? crm.brandSoft : crm.subtle}
                >
                  <Text
                    fontSize="10px"
                    fontWeight="700"
                    color={isToday ? crm.brand : crm.textMuted}
                    textTransform="uppercase"
                    letterSpacing="0.05em"
                  >
                    {DOW_LABELS[di]}
                  </Text>
                  <Flex
                    w="28px"
                    h="28px"
                    align="center"
                    justify="center"
                    borderRadius="full"
                    bg={isToday ? crm.brand : undefined}
                    color={isToday ? "white" : crm.textMuted}
                    fontWeight={isToday ? "700" : "400"}
                    fontSize="sm"
                  >
                    {day.day}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>

          {/* Scrollable time grid */}
          <Box overflowY="auto" maxH="600px">
            <Flex>
              {/* Time gutter */}
              <Box
                w={TIME_GUTTER_W}
                flexShrink={0}
                borderRight={`1px solid ${crm.border}`}
                bg={crm.surface}
              >
                {HOURS.map((h) => (
                  <Box
                    key={h}
                    h={`${HOUR_HEIGHT}px`}
                    borderBottom={`1px solid ${crm.border}`}
                    position="relative"
                  >
                    <Text
                      position="absolute"
                      top="-8px"
                      right="6px"
                      fontSize="10px"
                      color={crm.textFaint}
                      userSelect="none"
                      bg={crm.surface}
                      px="2px"
                    >
                      {String(h).padStart(2, "0")}:00
                    </Text>
                  </Box>
                ))}
              </Box>

              {/* Day columns */}
              {weekDays.map((day, di) => {
                const isoDate = day.toISODate()!;
                const isToday = isoDate === today;
                const dayVisits = visitsByDay[isoDate] ?? [];

                return (
                  <Box
                    key={di}
                    flex={1}
                    position="relative"
                    borderLeft={di > 0 ? `1px solid ${crm.border}` : undefined}
                    bg={isToday ? crm.brandSoft : undefined}
                  >
                    {/* Hour grid lines */}
                    {HOURS.map((h) => (
                      <Box
                        key={h}
                        h={`${HOUR_HEIGHT}px`}
                        borderBottom={`1px solid ${crm.border}`}
                      />
                    ))}

                    {/* Visit blocks — absolutely positioned by time */}
                    {dayVisits.map((v) => (
                      <VisitBlock
                        key={v.id}
                        visit={v}
                        onClick={() => onVisitClick(v)}
                      />
                    ))}
                  </Box>
                );
              })}
            </Flex>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VisitCalendar;
